import { StaticImageData } from 'next/image';
import { type ReactNode } from 'react';
import { z } from 'zod';

import { assemblyTypes, keys } from '@sola/contentful-model/keys.js';
import {
  genderIds,
  maxAge,
  minAge,
  patternFcpId,
  programCountryIsoCodes,
} from '@sola/contentful-model/lib/sac-values.js';

import { CommitmentFrequencyEnum } from '../dotcom/cart/commitments';
import { ChildAvailabilityItem, ChildDetails } from '../dotcom/children/child';
import { Campaign } from '../dotcom/shared/content/campaigns';
import { DisplayVariant } from './displays';
import { IconNamesSafe } from './icons/icon-names';
import { LOGO_NAMES } from './logos';

// @TODO: Is there a better way to validate this?
const ReactElement = z.custom<ReactNode>();

export const Templates = z.enum([
  keys.templates.bio,
  keys.templates.country,
  keys.templates.donate,
  keys.templates.giftCatalog,
]);
export type Templates = z.infer<typeof Templates>;

export const ContentfulLinkReference = z.object({
  type: z.string(),
  id: z.string(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  path: z.string().optional(),
});
export type ContentfulLinkReference = z.infer<typeof ContentfulLinkReference>;

export const ContentfulEntryReference = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  status: z.string().optional(),
  lastPublished: z.string().datetime().optional(),
  templateContentId: z.string().optional(),
  isIntendedForReuse: z.boolean().optional(),
});
export type ContentfulEntryReference = z.infer<typeof ContentfulEntryReference>;

export const EntryCardReference = z.object({
  type: z.string(),
  lastPublished: z.string().datetime().optional(),
  lastUpdated: z.string().datetime().optional(),
  id: z.string(),
});
export type EntryCardReference = z.infer<typeof EntryCardReference>;

export const ContentfulEntry = z.object({
  type: z.string(),
  status: z.string(),
  lastUpdated: z.string().datetime(),
  lastPublished: z.string().datetime().optional(),
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  urn: z.string(),
});
export type ContentfulEntry = z.infer<typeof ContentfulEntry>;

export const Columns = z.enum(['column1', 'column2']);
export type Columns = z.infer<typeof Columns>;

export const ColumnRatios = z.enum(['1-1', '1-2', '2-1']);
export type ColumnRatios = z.infer<typeof ColumnRatios>;

// @TODO: Integrate with Ark tokens?
export const ThemesEnum = z.enum(['white', 'lightBlue', 'darkBlue']);
export type ThemesEnum = z.infer<typeof ThemesEnum>;
export const ThemeSchema = z.object({
  value: ThemesEnum,
  label: z.string(),
});

export const formattedThemeNames = {
  [ThemesEnum.enum.lightBlue]: 'Theme Light',
  [ThemesEnum.enum.darkBlue]: 'Theme Dark',
  [ThemesEnum.enum.white]: 'Default',
};

export const Themes = {
  white: { value: 'white', label: 'Primary Color' },
  lightBlue: { value: 'lightBlue', label: 'Secondary Color' },
  darkBlue: { value: 'darkBlue', label: 'Accent Color' },
} as const;

export const BadgeColors = z.enum([
  'neutral',
  'brand',
  'positive',
  'warning',
  'negative',
  // Adding green back because removing it broke all the existing content.
  // @TODO: We need to identify which schema items require a migration script when we change them.
  'green',
]);
export type BadgeColors = z.infer<typeof BadgeColors>;

export const Image = z.object({
  url: z.string(),
  alt: z.string().optional(),
  ratio: z.string().optional(),
  height: z.number(),
  width: z.number(),
  focus: z.object({ x: z.string(), y: z.string() }).optional(),
});
export type ImageProps = z.infer<typeof Image>;

export const VideoProps = z.object({
  url: z.string(),
  autoplay: z.boolean().optional(),
  hasProgrammaticButton: z.boolean().optional(),
  isBackground: z.boolean().optional(),
  poster: Image.optional(),
  allowPlaying: z.boolean().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  duration: z.number().optional(),
  thumbnailUrl: z.string().optional(),
  title: z.string().optional(),
  uploadDate: z.string().optional(),
});
export type VideoProps = z.infer<typeof VideoProps>;

export const MediaProps = z
  .object({
    caption: z.string().optional(),
    image: Image.optional(),
    video: VideoProps.optional(),
  })
  .refine((data) => data.image || data.video, {
    message: "At least one of 'image' or 'video' must be provided",
  });
export type MediaProps = z.infer<typeof MediaProps>;

export const Link = z.object({
  title: z.string(),
  link: z.string(),
  target: z.string().optional(),
  isDownload: z.boolean().optional(),
});
export type Link = z.infer<typeof Link>;

export const Quotation = z.object({
  text: z.string().optional(),
  attribution: z.string().optional(),
  title: z.string().optional(),
  image: Image.optional(),
  primaryCTA: Link.optional(),
});
export type Quotation = z.infer<typeof Quotation>;

export const Logo = z.object({
  logo: LOGO_NAMES.optional(),
  alt: z.string().optional(),
});
export type Logo = z.infer<typeof Logo>;

export const Badge = z.object({
  text: z.string().optional(),
  color: BadgeColors.optional(),
});
export type Badge = z.infer<typeof Badge>;

export const SectionMeta = z.object({
  anchor: z.string().optional(),
  dividerBelow: z.boolean().default(false).optional(),
  fullWidth: z.boolean().default(false).optional(),
  layoutRatio: ColumnRatios.default(ColumnRatios.enum['1-1']),
  overlap: z.boolean().default(false).optional(),
  theme: z
    .string()
    .default(ThemesEnum.enum.white)
    .transform((theme) => {
      if (!Object.values(ThemesEnum.enum).includes(theme as ThemesEnum)) {
        return ThemesEnum.enum.white;
      }
      return theme as ThemesEnum;
    }),
});
export type SectionMeta = z.infer<typeof SectionMeta>;

const MenuItemBaseProps = z.object({
  seoLevel: z.number().int().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  icon: IconNamesSafe.optional(),
  link: z.string().optional(),
  target: z.string().optional(),
  logo: Logo.optional(),
  displayVariant: DisplayVariant.optional().nullable(),
  badge: Badge.optional(),
});

export const NavigationProps = MenuItemBaseProps.extend({
  items: MenuItemBaseProps.extend({
    items: MenuItemBaseProps.extend({
      items: MenuItemBaseProps.array().optional(),
    })
      .array()
      .optional(),
  })
    .array()
    .optional(),
});
export type NavigationProps = z.infer<typeof NavigationProps>;

export const FundOptionProps = z.object({
  value: z.coerce.number().default(5),
  label: z.string().optional().nullable(),
  badge: z.string().optional().nullable(),
  isDefault: z.boolean().optional().nullable(),
});
export type FundOptionProps = z.infer<typeof FundOptionProps>;

export const FundProps = z.object({
  // FUND
  id: z.string(),
  tcmId: z.string().optional(),
  fundTitle: z.string().optional(),
  fundCodeOneTime: z.string().optional(),
  fundCodeCommitment: z.string().optional(),
  interventionId: z.string().optional(),
  icon: z.string().optional(),
  minOneTimeAmount: z.coerce.number().optional(),
  donationLimitTotal: z.coerce.number().optional(),
  availableFrequencies: z
    .enum(Object.values(CommitmentFrequencyEnum) as [string, ...string[]])
    .array()
    .optional()
    .nullable(),
  // FUND SETTINGS
  suggestedAmount: z.coerce.number().min(5).optional(),
  suggestedAmountsOneTime: FundOptionProps.array().optional(),
  suggestedAmountsMonthly: FundOptionProps.array().optional(),
  suggestedAmountsAnnually: FundOptionProps.array().optional(),
  catalogAmount: z.number().optional(),
});
export type FundProps = z.infer<typeof FundProps>;

export const MergeData = z.object({
  campaignData: z.custom<Campaign>().optional(),
  childDetails: z.custom<ChildDetails>().optional(),
  childAvailability: z.custom<ChildAvailabilityItem>().optional(),
  cookies: z.custom<{ value: string; name: string }[]>().optional(),
  pathToSacPage: z.string().optional(),
  pathToChildBio: z.string().optional(),
  pageData: z
    .object({
      country: z.string().optional(),
      template: Templates.optional(),
    })
    .optional(),
});
export type MergeData = z.infer<typeof MergeData>;

export const FlexibleContentBaseProps = z.object({
  seoLevel: z.number().int().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  body: ReactElement.optional(),
  media: MediaProps.optional(),
  mediaLogo: MediaProps.optional(),
  primaryCTA: Link.optional(),
  secondaryCTA: Link.optional(),
  tertiaryCTA: Link.optional(),
  icon: IconNamesSafe.optional(),
  badge: Badge.optional(),
  quotation: Quotation.optional(),
  logo: Logo.optional(),
  displayDate: z.string().optional(),
  inline: z.boolean().default(false).optional(),
  fund: FundProps.optional(),
  mergeFieldData: MergeData.optional(),
});

export const SacFilterProps = FlexibleContentBaseProps.partial().extend({
  limit: z.number().int().positive().lt(1000).default(12).optional(),
  ageMax: z.number().min(minAge).max(maxAge).optional(),
  ageMin: z.number().min(minAge).max(maxAge).optional(),
  birthDate: z.string().datetime().optional(),
  centerId: z.string().regex(patternFcpId).optional(),
  countries: z.enum(programCountryIsoCodes).array().optional(),
  gender: z.enum(genderIds).optional(),
  disability: z.boolean().optional(),
  hiv: z.boolean().optional(),
  highlyVulnerable: z.boolean().optional(),
  longestWaiting: z.boolean().optional(),
  orphan: z.boolean().optional(),
  pathToSacPage: z.string().optional(),
  pathToChildBio: z.string().optional(),
});
export type SacFilterProps = z.infer<typeof SacFilterProps>;

const PageLink = z.object({
  sys: z.object({
    id: z.string(),
  }),
  path: z.string().optional().nullable(),
});
const ArticleLink = z.object({
  sys: z.object({
    id: z.string(),
  }),
  slug: z.string().optional().nullable(),
  taxonomyArticleType: z.string().optional().nullable(),
});
const DownloadLink = z.object({
  sys: z.object({ id: z.string() }),
  file: z
    .object({
      url: z.string().optional().nullable(),
      title: z.string().optional().nullable(),
      contentType: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
});
const ContentResponseBody = z.object({
  json: z.object({}).passthrough().optional().nullable(),
  links: z
    .object({
      entries: z.object({
        hyperlink: z
          .object({
            __typename: z.string(),
            ...PageLink.shape,
            ...ArticleLink.shape,
            ...DownloadLink.shape,
          })
          .nullable()
          .array()
          .optional()
          .nullable(),
        // block: z.custom<EmbedContent | EmbedMedia>().optional().nullable(),
      }),
    })
    .optional()
    .nullable(),
});
type ContentResponseBody = z.infer<typeof ContentResponseBody>;

export const PartnerProps = z.object({
  title: z.string().optional(),
  slug: z.string().optional(),
  body: ContentResponseBody.optional().nullable(),
  media: MediaProps.optional().nullable(),
  mediaLogo: MediaProps.optional().nullable(),
});

export type PartnerProps = z.infer<typeof PartnerProps>;

export const EmbedProps = z.object({
  url: z.string().optional(),
});
export type EmbedProps = z.infer<typeof EmbedProps>;

export const ContributorProps = z.object({
  name: z.string().optional(),
  position: z.string().optional(),
  image: Image.optional(),
});
export type ContributorProps = z.infer<typeof ContributorProps>;

export const DisplaySettings = z.object({
  alt: z.boolean().optional(),
});

export const FlexibleContentProps = FlexibleContentBaseProps.extend({
  items: FlexibleContentBaseProps.array().optional(),
}).merge(DisplaySettings);
export type FlexibleContentProps = z.infer<typeof FlexibleContentProps>;

export const ArticleProps = z.object({
  contentfulEntryID: z.string().optional(),
  seoLevel: z.number().int().optional(),
  title: z.string().optional(),
  summary: z.string().optional(),
  searchKeywords: z.string().optional(),
  publishDate: z.string().optional(),
  intro: ReactElement.optional(),
  body: ReactElement.optional(),
  conclusion: ReactElement.optional(),
  faq: FlexibleContentProps.optional(),
  primaryMedia: FlexibleContentProps.optional(),
  primaryCta: z.custom<FlexibleContentProps | SacFilterProps>().optional(),
  secondaryCta: FlexibleContentProps.optional(),
  sections: z
    .object({
      title: z.string().optional(),
      id: z.string().optional(),
    })
    .array()
    .optional(),
  contributors: z
    .object({
      name: z.string(),
      position: z.string().optional(),
      image: Image.optional(),
    })
    .array()
    .optional(),
  taxonomyProgramCountry: z.string().array().optional(),
  taxonomyProgramRegion: z.string().array().optional(),
  taxonomyArticleType: z.string().optional(),
  taxonomyFunnel: z.string().optional(),
  taxonomyArticleTheme: z.string().array().optional(),
  taxonomyTopic: z.string().array().optional(),
  modifiedDate: z.string().optional(),
});

export type ArticleProps = z.infer<typeof ArticleProps>;

export const SectionHeadingProps = z.object({
  title: z.string(),
  subtitle: z.string(),
});
export type SectionHeadingProps = z.infer<typeof SectionHeadingProps>;

export type AccountComponents = React.ComponentType<SectionHeadingProps>;

export const BlockProps = z.object({
  seoLevel: z.number().int().optional(),
  mergeFieldData: MergeData.optional(),
});
export type BlockProps = z.infer<typeof BlockProps>;
export type BlockComponents = React.ComponentType<BlockProps>;

export type ContentComponents =
  | React.ComponentType<FlexibleContentProps>
  | React.ComponentType<ArticleProps>
  | React.ComponentType<NavigationProps>
  | React.ComponentType<SacFilterProps>
  | React.ComponentType<EmbedProps>;

export const ComponentGroup = z.enum([
  'account',
  'advanced',
  'content',
  'opensearch',
]);
export type ComponentGroup = z.infer<typeof ComponentGroup>;

export type ComponentDefinition = {
  componentId: string;
  label: string;
  supportedContent: string[];
  lazyLoad?: boolean;
  /**
   * Indicates whether this component is an "advanced block" that requires
   * special handling in the page builder. Advanced blocks are typically:
   * - Added by admins through the "Add Block" action rather than "Add Component"
   * - Have extended/custom settings beyond standard content components
   * - Include specialized functionality like forms, gift catalogs, article lists, etc.
   * - May belong to the 'advanced' or 'account' component groups
   *
   * Examples include: articleList, giftCatalog, form components, and account-specific blocks
   * defined in keys.advancedComponents and keys.accountComponents
   */
  isAdvancedBlock?: boolean;
  group?: ComponentGroup;
  variants: {
    variantId: string;
    label: string;
    supportsAlternate: boolean;
    previewImage: ImageData | StaticImageData | string;
    altPreviewImage?: ImageData | StaticImageData | string;
    description?: string;
    component: BlockComponents | ContentComponents | AccountComponents;
  }[];
};

export const Variant = z.object({
  variantId: z.string(),
  label: z.string(),
  supportsAlternate: z.boolean(),
  previewImage: z.union([
    z.custom<ImageData | StaticImageData | string>(),
    z.string(),
  ]),
  description: z.string().optional(),
  component: z.custom<
    BlockComponents | ContentComponents | AccountComponents
  >(),
});
export type Variant = z.infer<typeof Variant>;

export const ComponentDefinition = z.object({
  componentId: z.string(),
  label: z.string(),
  supportedContent: z.array(z.string()),
  lazyLoad: z.boolean().optional(),
  isAdvancedBlock: z.boolean().optional(),
  variants: Variant.array(),
  group: ComponentGroup.optional(),
}) satisfies z.ZodType<ComponentDefinition>;

export const AdvancedBlocks = z.enum([
  ...Object.values(keys.advancedComponents),
  ...Object.values(keys.accountComponents),
] as [string, ...string[]]);
export type AdvancedBlocks = z.infer<typeof AdvancedBlocks>;

export const FormVariants = z.enum([
  'formCompassionSundayPlanningGuide',
  'formCompassionSundayRegistration',
  'formLeadChurch',
  'formCrypto',
]);
export type FormVariants = z.infer<typeof FormVariants>;

export const ContentTypes = z.enum([
  keys.contentTypes.flexibleContent,
  keys.contentTypes.navigation,
  keys.contentTypes.sacFilter,
  keys.contentTypes.embed,
]);

export const additionalSettings = z.object({
  experimentShow: z.string().optional(),
  experimentHide: z.string().optional(),
});

export const additionalSettingsDefault = additionalSettings.extend({
  type: z.literal('default'),
});

export const additionalSettingsArticleList = additionalSettings.extend({
  type: z.literal(keys.advancedComponents.articleList),
  articleType: z.string().optional(),
  limit: z.number().int().optional().default(4),
});

export const additionalSettingsGiftCatalog = additionalSettings.extend({
  type: z.literal(keys.advancedComponents.giftCatalog),
  number: z.number().int().optional(),
});

export const Component = z.object({
  designComponent: z.string().default('default'),
  designVariant: z.string().default('default'),
  useAlternate: z.boolean().default(false),
  contentEntry: ContentfulEntryReference.optional(),
  templateCustomContentPosition: z.number().optional(),
  templateGlobalContentPosition: z.number().optional(),
  templateCustomContentId: z.string().optional(),
  templateGlobalContentId: z.string().optional(),
  additionalSettings: z
    .discriminatedUnion('type', [
      additionalSettingsDefault,
      additionalSettingsArticleList,
      additionalSettingsGiftCatalog,
    ])
    .catch({ type: 'default' })
    .optional(),
});
export type Component = z.infer<typeof Component>;

export const Section = z.object({
  column1: Component.array(),
  column2: Component.array(),
  meta: SectionMeta,
});
export type Section = z.infer<typeof Section>;

const AssemblyTemplate = z.object({
  templateId: Templates,
  customContent: z
    .array(ContentfulEntryReference.optional().nullable())
    .optional()
    .nullable(),
});
export type AssemblyTemplate = z.infer<typeof AssemblyTemplate>;

export const AssemblySchema = z.object({
  template: AssemblyTemplate.optional(),
  sections: Section.array(),
});
export type AssemblySchema = z.infer<typeof AssemblySchema>;

export const Events = z.enum([
  'addSection',
  'setSectionMeta',
  'moveSection',
  'deleteSection',
  'addComponent',
  'addBlock',
  'moveComponent',
  'deleteComponent',
  'setComponentDisplay',
  'setComponentContent',
  'clearComponentContent',
  'setTemplateContent',
  'clearTemplateContent',
  'setAssemblyTemplate',
  'setAssemblySections',
]);
export type Events = z.infer<typeof Events>;

export const ActionSectionAdd = z.object({
  event: z.literal(Events.enum.addSection),
});
export type ActionSectionAdd = z.infer<typeof ActionSectionAdd>;

export const ActionSectionSetMeta = z
  .object({
    event: z.literal(Events.enum.setSectionMeta),
    sectionIndex: z.number().int(),
  })
  .extend(SectionMeta.partial().shape);
export type ActionSectionSetMeta = z.infer<typeof ActionSectionSetMeta>;

export const ActionSectionMove = z.object({
  event: z.literal(Events.enum.moveSection),
  sectionIndex: z.number().int(),
  toIndex: z.number().int(),
});
export type ActionSectionMove = z.infer<typeof ActionSectionMove>;

export const ActionSectionDelete = z.object({
  event: z.literal(Events.enum.deleteSection),
  sectionIndex: z.number().int(),
});
export type ActionSectionDelete = z.infer<typeof ActionSectionDelete>;

export const ActionComponentTypes = z.enum([...assemblyTypes]);

export const ActionComponentAdd = z.object({
  event: z.literal(Events.enum.addComponent),
  sectionIndex: z.number().int(),
  column: Columns,
  type: ActionComponentTypes,
});
export type ActionComponentAdd = z.infer<typeof ActionComponentAdd>;

export const ActionBlockAdd = z.object({
  event: z.literal(Events.enum.addBlock),
  sectionIndex: z.number().int(),
  column: Columns,
  block: AdvancedBlocks.optional(),
});
export type ActionBlockAdd = z.infer<typeof ActionBlockAdd>;

export const ActionComponentMove = z.object({
  event: z.literal(Events.enum.moveComponent),
  sectionIndex: z.number().int(),
  fromColumn: Columns,
  toColumn: Columns,
  componentIndex: z.number().int(),
  toIndex: z.number().int(),
});
export type ActionComponentMove = z.infer<typeof ActionComponentMove>;

export const ActionComponentDelete = z.object({
  event: z.literal(Events.enum.deleteComponent),
  sectionIndex: z.number().int(),
  column: Columns,
  componentIndex: z.number().int(),
});
export type ActionComponentDelete = z.infer<typeof ActionComponentDelete>;

export const ActionComponentSetDisplay = z
  .object({
    event: z.literal(Events.enum.setComponentDisplay),
    sectionIndex: z.number().int(),
    column: Columns,
    componentIndex: z.number().int(),
  })
  .extend(Component.omit({ contentEntry: true }).shape);
export type ActionComponentSetDisplay = z.infer<
  typeof ActionComponentSetDisplay
>;

export const ActionComponentSetContent = z
  .object({
    event: z.literal(Events.enum.setComponentContent),
    sectionIndex: z.number().int(),
    column: Columns,
    componentIndex: z.number().int(),
    componentDefinitions: z.array(ComponentDefinition),
  })
  .extend(Component.pick({ contentEntry: true }).required().shape);
export type ActionComponentSetContent = z.infer<
  typeof ActionComponentSetContent
>;

export const ActionComponentClearContent = z.object({
  event: z.literal(Events.enum.clearComponentContent),
  sectionIndex: z.number().int(),
  column: Columns,
  componentIndex: z.number().int(),
});
export type ActionComponentClearContent = z.infer<
  typeof ActionComponentClearContent
>;

export const ActionTemplateSetContent = z
  .object({
    event: z.literal(Events.enum.setTemplateContent),
    templateContentId: z.string(),
    templateId: z.string().optional(),
  })
  .extend(Component.pick({ contentEntry: true }).required().shape);
export type ActionTemplateSetContent = z.infer<typeof ActionTemplateSetContent>;

export const ActionTemplateClearContent = z.object({
  event: z.literal(Events.enum.clearTemplateContent),
  templateContentId: z.string(),
});
export type ActionTemplateClearContent = z.infer<
  typeof ActionTemplateClearContent
>;

export const ActionSetAssemblyTemplate = z.object({
  event: z.literal(Events.enum.setAssemblyTemplate),
  updatedAssembly: AssemblySchema,
});
export type ActionSetAssemblyTemplate = z.infer<
  typeof ActionSetAssemblyTemplate
>;

export const ActionSetAssemblySections = z.object({
  event: z.literal(Events.enum.setAssemblySections),
  updatedAssembly: AssemblySchema,
});
export type ActionSetAssemblySections = z.infer<
  typeof ActionSetAssemblySections
>;

export const Action = z.discriminatedUnion('event', [
  ActionBlockAdd,
  ActionComponentAdd,
  ActionComponentClearContent,
  ActionComponentDelete,
  ActionComponentMove,
  ActionComponentSetContent,
  ActionComponentSetDisplay,
  ActionSectionAdd,
  ActionSectionDelete,
  ActionSectionMove,
  ActionSectionSetMeta,
  ActionTemplateClearContent,
  ActionTemplateSetContent,
  ActionSetAssemblyTemplate,
  ActionSetAssemblySections,
]);
export type Action = z.infer<typeof Action>;

export const componentTypeLoadingLabel = 'loading...';

export const defaultDesignComponent = {
  [keys.contentTypes.flexibleContent]: 'basicContent',
  [keys.contentTypes.navigation]: 'embeddedNav',
  [keys.contentTypes.sacFilter]: 'sacList',
  [keys.contentTypes.embed]: 'embed',
  [keys.accountComponents.default]: 'none',
};
export const defaultDesignVariant = {
  [keys.contentTypes.flexibleContent]: 'main',
  [keys.contentTypes.navigation]: 'main',
  [keys.contentTypes.sacFilter]: 'core',
  [keys.contentTypes.embed]: 'main',
};

export const defaultContentType = keys.contentTypes.flexibleContent;
export const getDefaultComponent = (type: string = defaultContentType) => {
  try {
    return Component.parse({
      designComponent: defaultDesignComponent[type],
      designVariant: defaultDesignVariant[type],
      useAlternate: false,
    } as Component);
  } catch {
    return Component.parse({
      designComponent:
        defaultDesignComponent[keys.contentTypes.flexibleContent],
      designVariant: defaultDesignVariant[keys.contentTypes.flexibleContent],
      useAlternate: false,
    } as Component);
  }
};

export const defaultComponent = getDefaultComponent(defaultContentType);

export const defaultSection = Section.parse({
  column1: [],
  column2: [],
  meta: {
    anchor: undefined,
    theme: ThemesEnum.enum.white,
    dividerBelow: false,
    fullWidth: false,
    layoutRatio: ColumnRatios.enum['1-1'],
    overlap: false,
  },
} as Section);
export const defaultAssembly: AssemblySchema = { sections: [] };

export const TemplateContentGlobal = z.partialRecord(
  Templates,
  z.object({
    content: z
      .array(ContentfulEntryReference.optional().nullable())
      .optional()
      .nullable(),
    parentPage: ContentfulEntryReference.optional().nullable(),
  }),
);
export type TemplateContentGlobal = z.infer<typeof TemplateContentGlobal>;

const SeoKeyword = z.object({
  keyword: z.string(),
  count: z.number(),
  density: z.number(),
});
export type SeoKeyword = z.infer<typeof SeoKeyword>;

const SeoKeyphrase = z.object({
  phrase: z.string(),
  count: z.number(),
  countMax: z.number(),
  density: z.number(),
});
export type SeoKeyphrase = z.infer<typeof SeoKeyphrase>;

const SeoAnalysis = z.object({
  keyphraseAnalysis: SeoKeyphrase.optional(),
  keywordAnalysis: z
    .object({
      keywordMetrics: z.array(SeoKeyword).default([]),
      keywordRecommendations: z.array(SeoKeyword).default([]),
    })
    .optional(),
  readability: z.number(),
});
export type SeoAnalysis = z.infer<typeof SeoAnalysis>;

export const sectionColumnComponentRegex =
  /section(\d+)(?:column(\d+)(?:component(\d+))?)?/;

export enum ARTICLE_URL_PARAMS {
  PAGE = 'page',
  COUNTRY = 'country',
  THEME = 'theme',
  TOPIC = 'topic',
  LIMIT = 'limit',
  SORTBY = 'sortby',
  TAB = 'tab',
}

export enum GIFT_CATALOG_URL_PARAMS {
  PAGE = 'page',
  CATEGORY = 'category',
  PROMOTION = 'promotion',
  PRICE_MIN = 'priceMin',
  PRICE_MAX = 'priceMax',
  SORTBY = 'sortby',
}

export const GiftCatalogSortEnum = z.enum([
  'mostNeeded',
  'priceLowToHigh',
  'priceHighToLow',
  'alphabeticalAZ',
  'alphabeticalZA',
]);
export type GiftCatalogSortEnum = z.infer<typeof GiftCatalogSortEnum>;

export enum SAC_URL_PARAMS {
  GENDER = 'gender',
  COUNTRIES = 'countries',
  AGE_MIN = 'ageMin',
  AGE_MAX = 'ageMax',
  BIRTH_DAY = 'birthDay',
  BIRTH_MONTH = 'birthMonth',
  ORPHANED_CHILD = 'orphanedChild',
  HAS_DISABILITIES = 'hasDisabilities',
  VULNERABLE_EXPLOITATION = 'vulnerableExploitation',
  AFFECTED_HIV = 'affectedHiv',
  FILTERS = 'filters',
}

export const ChildBioQueryParams = z.object({
  gid: z.string().optional(),
  source: z.string().optional(),
});
export type ChildBioQueryParams = z.infer<typeof ChildBioQueryParams>;

export const MergeFieldQueryParams = ChildBioQueryParams.extend({
  artist: z.string().optional(),
});
export type MergeFieldQueryParams = z.infer<typeof MergeFieldQueryParams>;
